import { useState, useRef, useEffect } from 'react';
import type { GlobalAuditAction, GlobalAuditModule, AuditSeverity } from '@/mocks/saGlobalAudit';
import { actionLabels, moduleLabels, severityLabels, userOptions } from '@/mocks/saGlobalAudit';

interface AuditGlobalFiltersProps {
  searchTerm: string;
  onSearchChange: (v: string) => void;
  activeUser: string;
  onUserChange: (v: string) => void;
  activeModule: string;
  onModuleChange: (v: string) => void;
  activeAction: string;
  onActionChange: (v: string) => void;
  activeSeverity: string;
  onSeverityChange: (v: string) => void;
  activeResult: string;
  onResultChange: (v: string) => void;
  dateFrom: string;
  onDateFromChange: (v: string) => void;
  dateTo: string;
  onDateToChange: (v: string) => void;
  ipFilter: string;
  onIpFilterChange: (v: string) => void;
  userCounts: Record<string, number>;
  moduleCounts: Record<string, number>;
  actionCounts: Record<string, number>;
  severityCounts: Record<string, number>;
  resultCounts: Record<string, number>;
}

const moduleKeys: string[] = ['all', 'auth', 'radios', 'plans', 'frequencies', 'moderation', 'settings', 'content', 'billing', 'integrations', 'security', 'storage', 'feature_flags', 'api', 'notifications'];
const actionKeys: string[] = ['all', 'login', 'logout', 'create', 'edit', 'delete', 'approve', 'reject', 'permission_change', 'config_change', 'critical_error', 'plan_change', 'feature_flag', 'export', 'bulk_operation'];
const severityKeys: string[] = ['all', 'info', 'warning', 'critical'];
const resultKeys: string[] = ['all', 'success', 'failure'];

const moduleIconMap: Record<string, string> = {
  all: 'ri-filter-line', auth: 'ri-shield-keyhole-line', radios: 'ri-radio-line', plans: 'ri-stack-line',
  frequencies: 'ri-radar-line', moderation: 'ri-shield-check-line', settings: 'ri-settings-3-line',
  content: 'ri-file-list-3-line', billing: 'ri-bank-card-line', integrations: 'ri-plug-line',
  security: 'ri-lock-line', storage: 'ri-hard-drive-2-line', feature_flags: 'ri-flag-line',
  api: 'ri-terminal-box-line', notifications: 'ri-notification-3-line',
};

const severityIconMap: Record<string, string> = {
  all: 'ri-filter-line', info: 'ri-information-line', warning: 'ri-alert-line', critical: 'ri-error-warning-line',
};

const resultIconMap: Record<string, string> = { all: 'ri-filter-line', success: 'ri-check-line', failure: 'ri-close-circle-line' };
const resultLabelMap: Record<string, string> = { all: 'Todos', success: 'Éxito', failure: 'Fallido' };

export default function AuditGlobalFilters({
  searchTerm,
  onSearchChange,
  activeUser,
  onUserChange,
  activeModule,
  onModuleChange,
  activeAction,
  onActionChange,
  activeSeverity,
  onSeverityChange,
  activeResult,
  onResultChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  ipFilter,
  onIpFilterChange,
  userCounts,
  moduleCounts,
  actionCounts,
  severityCounts,
  resultCounts,
}: AuditGlobalFiltersProps) {
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target as Node)) {
        setUserDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedUser = userOptions.find((u) => u.id === activeUser);

  return (
    <div className="space-y-3">
      {/* Main row: search + user + quick filters */}
      <div className="flex flex-col lg:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-0">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar por usuario, acción, objetivo, IP..."
            className="w-full pl-8 pr-8 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
            >
              <i className="ri-close-line text-foreground-500 text-[10px]"></i>
            </button>
          )}
        </div>

        {/* User dropdown */}
        <div className="relative" ref={userDropdownRef}>
          <button
            onClick={() => setUserDropdownOpen(!userDropdownOpen)}
            className="flex items-center gap-2 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-700 hover:border-foreground-300 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-user-3-line text-foreground-400 text-sm"></i>
            <span className="max-w-[120px] truncate">{activeUser === 'all' ? 'Todos los usuarios' : selectedUser?.name ?? 'Usuario'}</span>
            <span className="text-foreground-300 text-[9px] ml-1">({userCounts[activeUser] ?? 0})</span>
            <i className={`ri-arrow-down-s-line text-foreground-400 text-xs transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`}></i>
          </button>
          {userDropdownOpen && (
            <div className="absolute top-full mt-1 left-0 w-56 bg-background-50 border border-background-200 rounded-xl shadow-lg z-30 max-h-60 overflow-y-auto">
              <button
                onClick={() => { onUserChange('all'); setUserDropdownOpen(false); }}
                className={`w-full text-left px-3 py-2 text-xs cursor-pointer transition-colors flex items-center justify-between ${activeUser === 'all' ? 'bg-background-100 font-semibold text-foreground-800' : 'text-foreground-600 hover:bg-background-100/50'}`}
              >
                <span>Todos los usuarios</span>
                <span className="text-foreground-300 text-[9px]">{userCounts.all ?? 0}</span>
              </button>
              {userOptions.map((u) => (
                <button
                  key={u.id}
                  onClick={() => { onUserChange(u.id); setUserDropdownOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-xs cursor-pointer transition-colors flex items-center justify-between ${activeUser === u.id ? 'bg-background-100 font-semibold text-foreground-800' : 'text-foreground-600 hover:bg-background-100/50'}`}
                >
                  <div>
                    <span>{u.name}</span>
                    <span className="text-foreground-400 text-[9px] ml-1.5">{u.role}</span>
                  </div>
                  <span className="text-foreground-300 text-[9px]">{userCounts[u.id] ?? 0}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Toggle more filters */}
        <button
          onClick={() => setShowMoreFilters(!showMoreFilters)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
            showMoreFilters
              ? 'bg-foreground-950 text-background-50 border-foreground-950'
              : 'bg-background-50 text-foreground-600 border-background-200 hover:border-foreground-300'
          }`}
        >
          <i className="ri-filter-3-line text-sm"></i>
          <span>Filtros</span>
          {(activeModule !== 'all' || activeAction !== 'all' || activeSeverity !== 'all' || activeResult !== 'all' || dateFrom || dateTo || ipFilter) && (
            <span className="w-2 h-2 rounded-full bg-amber-400"></span>
          )}
        </button>
      </div>

      {/* Chip filters: module + severity + result */}
      <div className="flex flex-col gap-2.5">
        {/* Module chips */}
        <div>
          <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1.5">Módulo</p>
          <div className="flex items-center gap-1 flex-wrap">
            {moduleKeys.slice(0, 9).map((key) => (
              <button
                key={key}
                onClick={() => onModuleChange(key)}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeModule === key
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-foreground-300'
                }`}
                title={moduleLabels[key as GlobalAuditModule] ?? 'Todos'}
              >
                <i className={`${moduleIconMap[key]} text-[10px]`}></i>
                {key === 'all' ? 'Todos' : moduleLabels[key as GlobalAuditModule]}
                <span className={`ml-0.5 text-[9px] ${activeModule === key ? 'text-background-50/70' : 'text-foreground-300'}`}>{moduleCounts[key] ?? 0}</span>
              </button>
            ))}
            {moduleKeys.length > 9 && (
              <button className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-background-50 text-foreground-500 border border-background-200 hover:border-foreground-300 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-more-line text-[10px]"></i>
                +{moduleKeys.length - 9}
              </button>
            )}
          </div>
        </div>

        {/* Action chips */}
        <div>
          <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1.5">Acción</p>
          <div className="flex items-center gap-1 flex-wrap">
            {actionKeys.slice(0, 10).map((key) => (
              <button
                key={key}
                onClick={() => onActionChange(key)}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeAction === key
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-foreground-300'
                }`}
              >
                {key === 'all' ? 'Todas' : actionLabels[key as GlobalAuditAction]}
                <span className={`ml-0.5 text-[9px] ${activeAction === key ? 'text-background-50/70' : 'text-foreground-300'}`}>{actionCounts[key] ?? 0}</span>
              </button>
            ))}
            {actionKeys.length > 10 && (
              <button className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-background-50 text-foreground-500 border border-background-200 hover:border-foreground-300 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-more-line text-[10px]"></i>
                +{actionKeys.length - 10}
              </button>
            )}
          </div>
        </div>

        {/* Severity + Result row */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div>
            <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1.5">Severidad</p>
            <div className="flex items-center gap-1 flex-wrap">
              {severityKeys.map((key) => (
                <button
                  key={key}
                  onClick={() => onSeverityChange(key)}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeSeverity === key
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-foreground-300'
                  }`}
                >
                  <i className={`${severityIconMap[key]} text-[10px]`}></i>
                  {key === 'all' ? 'Todas' : severityLabels[key as AuditSeverity]}
                  <span className={`ml-0.5 text-[9px] ${activeSeverity === key ? 'text-background-50/70' : 'text-foreground-300'}`}>{severityCounts[key] ?? 0}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1.5">Resultado</p>
            <div className="flex items-center gap-1 flex-wrap">
              {resultKeys.map((key) => (
                <button
                  key={key}
                  onClick={() => onResultChange(key)}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeResult === key
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-foreground-300'
                  }`}
                >
                  <i className={`${resultIconMap[key]} text-[10px]`}></i>
                  {resultLabelMap[key]}
                  <span className={`ml-0.5 text-[9px] ${activeResult === key ? 'text-background-50/70' : 'text-foreground-300'}`}>{resultCounts[key] ?? 0}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded filters panel */}
      {showMoreFilters && (
        <div className="bg-background-50 border border-background-200 rounded-2xl p-4 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-equalizer-line text-foreground-500 text-sm"></i>
            <span className="text-foreground-800 text-xs font-semibold">Filtros avanzados</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Date from */}
            <div>
              <label className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider block mb-1">Desde</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => onDateFromChange(e.target.value)}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
              />
            </div>
            {/* Date to */}
            <div>
              <label className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider block mb-1">Hasta</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => onDateToChange(e.target.value)}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
              />
            </div>
            {/* IP filter */}
            <div>
              <label className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider block mb-1">Dirección IP</label>
              <input
                type="text"
                value={ipFilter}
                onChange={(e) => onIpFilterChange(e.target.value)}
                placeholder="190.124.45..."
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 font-mono outline-none focus:border-foreground-300 transition-colors"
              />
            </div>
            {/* Reset all */}
            <div className="flex items-end">
              <button
                onClick={() => {
                  onUserChange('all');
                  onModuleChange('all');
                  onActionChange('all');
                  onSeverityChange('all');
                  onResultChange('all');
                  onDateFromChange('');
                  onDateToChange('');
                  onIpFilterChange('');
                  onSearchChange('');
                }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line text-sm"></i>
                Limpiar filtros
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}